import styled from 'styled-components';
import { SmallPortalTooltip } from '../../Tooltip';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
  TooltipSmallLabel,
} from './Tooltip';
import { CellContext } from '@tanstack/react-table';
import { LearnerProgress } from '.';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TopicPill, TopicText } from '../TopicPill';
import { useTheme } from 'styled-components';
import React from 'react';
dayjs.extend(utc);

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
`;

const SpacedTooltipTitle = styled(TooltipTitle)`
  margin-top: 8px;
`;

export default function MentorNotesCell(
  info: CellContext<LearnerProgress, unknown>
) {
  const theme = useTheme();

  const mentor = info.row.original.mentor;
  const officeHours = info.row.original.officeHours;

  if (!mentor || !officeHours?.officeHours?.length) {
    return '-';
  }

  const noteRefreshTime = dayjs
    .utc()
    .hour(8)
    .minute(0)
    .second(0)
    .local()
    .format('h:mm A [GMT]Z');

  const notesSummary = officeHours.notesSummary ?? 'No summary generated yet';
  const notesSummaryLastUpdatedAt = officeHours.notesLastUpdatedAt ?? null;
  const notesSummaryLastUpdatedAtLocal = notesSummaryLastUpdatedAt
    ? dayjs
        .utc(notesSummaryLastUpdatedAt)
        .local()
        .format('MMM D, YYYY HH:mm [GMT]Z')
    : null;

  return (
    <Column>
      <Row>
        <>
          <TopicPill
            backgroundColor={theme.colors.bgActive}
            data-tooltip-id={`mentor-notes-${info.row.id}-summary`}
          >
            <TopicText>Summary</TopicText>
          </TopicPill>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="${`mentor-notes-${info.row.id}-summary`}"]`}
          >
            <TooltipContent>
              <TooltipTitle>Summary</TooltipTitle>
              {notesSummary
                .split('\n')
                .filter(Boolean)
                .map((paragraph, i, arr) => (
                  <React.Fragment key={`${info.row.id}-paragraph-${i}`}>
                    <TooltipDetail>{paragraph}</TooltipDetail>
                    {i < arr.length - 1 && <br />}
                  </React.Fragment>
                ))}

              <TooltipSmallLabel style={{ marginTop: 16 }}>
                Summaries refresh daily at {noteRefreshTime}
              </TooltipSmallLabel>
              {notesSummaryLastUpdatedAt && (
                <TooltipSmallLabel style={{ marginTop: 8 }}>
                  Last updated at: {notesSummaryLastUpdatedAtLocal}
                </TooltipSmallLabel>
              )}
            </TooltipContent>
          </SmallPortalTooltip>
        </>
        {officeHours.officeHours.map((oh, index) => {
          const tooltipId = `mentor-notes-${info.row.id}-${index}`;
          const normalizedTimestamp = dayjs
            .utc(oh.timestamp)
            .local()
            .format('MMM D, YYYY HH:mm [GMT]Z');
          const isInTheFuture = dayjs.utc(oh.timestamp).isAfter(dayjs.utc());

          const didAttend = oh.didAttend;
          const goalsNote = oh.notes?.goals;
          const challengesNote = oh.notes?.challenges;
          const insightsNote = oh.notes?.insights;

          return (
            <React.Fragment key={`${info.row.id}-office-hour-${index}`}>
              <TopicPill
                backgroundColor={theme.colors.bgDefault}
                data-tooltip-id={tooltipId}
              >
                <TopicText>{index + 1}</TopicText>
              </TopicPill>
              <SmallPortalTooltip
                anchorSelect={`[data-tooltip-id="${tooltipId}"]`}
              >
                <TooltipContent>
                  <TooltipTitle>Office Hour {index + 1}</TooltipTitle>
                  <TooltipDetail>{normalizedTimestamp}</TooltipDetail>
                  <SpacedTooltipTitle>Attendance</SpacedTooltipTitle>
                  <TooltipDetail>
                    {isInTheFuture
                      ? 'Call is in the future'
                      : didAttend
                      ? 'Attended'
                      : 'Not Attended'}
                  </TooltipDetail>
                  {!isInTheFuture && (
                    <>
                      <SpacedTooltipTitle>Goals</SpacedTooltipTitle>
                      <TooltipDetail>{goalsNote || '-'}</TooltipDetail>
                      <SpacedTooltipTitle>Challenges</SpacedTooltipTitle>
                      <TooltipDetail>{challengesNote || '-'}</TooltipDetail>
                      <SpacedTooltipTitle>Insights</SpacedTooltipTitle>
                      <TooltipDetail>{insightsNote || '-'}</TooltipDetail>
                    </>
                  )}
                </TooltipContent>
              </SmallPortalTooltip>
            </React.Fragment>
          );
        })}
      </Row>
    </Column>
  );
}
